import { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronDown, ChevronRight, History, Loader2, MinusCircle, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '~/api/trpc';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type CustomerGiftCardsTableProps = {
    customerId: number;
};

type GiftCardRow = {
    id: string;
    giftCardNumber: string;
    name: string;
    type: string;
    initialValueEur: number;
    remainingBalance: number;
    usedAmount: number;
    buyerCustomer: { id: number; firstName: string; lastName: string; email: string | null } | null;
    recipientCustomer: { id: number; firstName: string; lastName: string; email: string | null } | null;
    invoiceId: string;
    invoiceNumber: string;
    sourceBillingDraftId: string | null;
    sourceBillingDraftNumber: string | null;
    createdAt: string;
    deletedAt: string | null;
    transactionCount: number;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

const ExpandedRowContent = ({
    giftCard,
    isAdmin,
}: {
    giftCard: GiftCardRow;
    isAdmin: boolean;
}) => {
    const navigate = useNavigate();
    const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
    const [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
    const [adjustmentReason, setAdjustmentReason] = useState('');
    const trpcUtils = trpc.useUtils();

    const { data: transactions, isLoading } = trpc.giftCards.getTransactions.useQuery(
        { giftCardId: giftCard.id }
    );

    const adjustMutation = trpc.giftCards.adjust.useMutation({
        onSuccess: () => {
            toast.success('Gutscheinkarte wurde angepasst');
            trpcUtils.giftCards.getByCustomer.invalidate();
            trpcUtils.giftCards.getTransactions.invalidate({ giftCardId: giftCard.id });
            setShowAdjustmentForm(false);
            setAdjustmentAmount('');
            setAdjustmentReason('');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleAdjust = () => {
        const amount = parseFloat(adjustmentAmount);
        if (!amount || amount === 0 || !adjustmentReason.trim()) {
            return;
        }
        adjustMutation.mutate({
            giftCardId: giftCard.id,
            amountEur: amount,
            reason: adjustmentReason.trim(),
        });
    };

    return (
        <div className='p-4 bg-muted/30 space-y-4'>
            {/* Transactions */}
            <div className='flex items-center gap-2'>
                <History className='h-4 w-4' />
                <span className='text-sm font-medium'>
                    Transaktionen ({giftCard.transactionCount})
                </span>
            </div>

            {isLoading ? (
                <div className='space-y-1'>
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className='h-8 w-full' />
                    ))}
                </div>
            ) : transactions && transactions.length > 0 ? (
                <div className='space-y-1 max-h-40 overflow-y-auto'>
                    {transactions.map((tx) => (
                        <div
                            key={tx.id}
                            className='flex justify-between items-center text-xs py-2 px-3 bg-background rounded border'
                        >
                            <div className='flex items-center gap-2 flex-wrap'>
                                <span className={Number(tx.amountEur) > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                    {Number(tx.amountEur) > 0 ? '+' : ''}{formatCurrency(Number(tx.amountEur))}
                                </span>
                                <span className='text-muted-foreground'>
                                    {tx.type === 'REDEMPTION' && 'Eingelöst'}
                                    {tx.type === 'REFUND' && 'Erstattet'}
                                    {tx.type === 'ADJUSTMENT' && 'Korrektur'}
                                </span>
                                {tx.payment?.billingDraft && (
                                    <span
                                        className='font-mono text-primary cursor-pointer hover:underline'
                                        onClick={() => navigate(`/billing-draft/${tx.payment!.billingDraft!.id}`)}
                                    >
                                        Entwurf {tx.payment.billingDraft.billingDraftNumber}
                                    </span>
                                )}
                                {tx.description && (
                                    <span className='text-muted-foreground'>- {tx.description}</span>
                                )}
                            </div>
                            <span className='text-muted-foreground shrink-0 ml-2'>
                                {format(new Date(tx.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className='text-sm text-muted-foreground'>
                    Noch keine Transaktionen
                </p>
            )}

            {/* Admin adjustment section */}
            {/*
            {isAdmin && !giftCard.deletedAt && (
                <>
                    <Separator />
                    {!showAdjustmentForm ? (
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => setShowAdjustmentForm(true)}
                        >
                            Manuell anpassen
                        </Button>
                    ) : (
                        <div className='space-y-3 p-3 border rounded-lg bg-background'>
                            <div className='space-y-2'>
                                <Label>Betrag hinzufügen/entfernen (€)</Label>
                                <div className='flex items-center gap-2'>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => {
                                            const current = parseFloat(adjustmentAmount) || 0;
                                            setAdjustmentAmount((current - 1).toFixed(2));
                                        }}
                                    >
                                        <MinusCircle className='h-4 w-4' />
                                    </Button>
                                    <Input
                                        type='number'
                                        step='0.01'
                                        value={adjustmentAmount}
                                        onChange={(e) => setAdjustmentAmount(e.target.value)}
                                        placeholder='0.00'
                                        className='w-28 text-center'
                                    />
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => {
                                            const current = parseFloat(adjustmentAmount) || 0;
                                            setAdjustmentAmount((current + 1).toFixed(2));
                                        }}
                                    >
                                        <PlusCircle className='h-4 w-4' />
                                    </Button>
                                </div>
                                <p className='text-xs text-muted-foreground'>
                                    Positiv = Guthaben aufladen, Negativ = Guthaben abziehen
                                </p>
                            </div>
                            <div className='space-y-2'>
                                <Label>Begründung *</Label>
                                <Textarea
                                    value={adjustmentReason}
                                    onChange={(e) => setAdjustmentReason(e.target.value)}
                                    placeholder='Grund für die Anpassung...'
                                    rows={2}
                                />
                            </div>
                            <div className='flex gap-2'>
                                <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() => {
                                        setShowAdjustmentForm(false);
                                        setAdjustmentAmount('');
                                        setAdjustmentReason('');
                                    }}
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    size='sm'
                                    onClick={handleAdjust}
                                    disabled={!adjustmentAmount || parseFloat(adjustmentAmount) === 0 || !adjustmentReason.trim() || adjustMutation.isPending}
                                >
                                    {adjustMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                    Anpassen
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
            */}
        </div>
    );
};

const CustomerGiftCardsTable = ({ customerId }: CustomerGiftCardsTableProps) => {
    const navigate = useNavigate();
    const { user } = useUserStore();
    const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.OPERATOR;

    const { data, isLoading } = trpc.giftCards.getByCustomer.useQuery({ customerId });
    const giftCards = (data?.data ?? []) as GiftCardRow[];

    const columns: ColumnDef<GiftCardRow>[] = [
        {
            id: 'expander',
            header: () => null,
            cell: ({ row }) => (
                <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                >
                    {row.getIsExpanded() ? (
                        <ChevronDown className='h-4 w-4' />
                    ) : (
                        <ChevronRight className='h-4 w-4' />
                    )}
                </Button>
            ),
        },
        {
            accessorKey: 'createdAt',
            header: 'Datum',
            cell: ({ row }) => (
                <span className='text-xs'>
                    {format(new Date(row.original.createdAt), 'dd.MM.yyyy', { locale: de })}
                </span>
            ),
        },
        {
            accessorKey: 'giftCardNumber',
            header: 'Nummer',
            cell: ({ row }) => (
                <span className='font-mono text-xs'>{row.original.giftCardNumber}</span>
            ),
        },
        {
            accessorKey: 'name',
            header: 'Name',
            cell: ({ row }) => (
                <span className='font-medium text-sm'>{row.original.name}</span>
            ),
        },
        {
            accessorKey: 'invoiceNumber',
            header: 'Rechnung',
            cell: ({ row }) => (
                <div
                    className='font-mono text-xs cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/invoice/${row.original.invoiceId}`);
                    }}
                >
                    {row.original.invoiceNumber}
                </div>
            ),
        },
        {
            id: 'billingDraft',
            header: 'Entwurf',
            cell: ({ row }) => {
                if (!row.original.sourceBillingDraftId) {
                    return <span className='text-xs text-muted-foreground'>-</span>;
                }
                return (
                    <div
                        className='font-mono text-xs cursor-pointer hover:underline text-primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/billing-draft/${row.original.sourceBillingDraftId}`);
                        }}
                    >
                        {row.original.sourceBillingDraftNumber}
                    </div>
                );
            },
        },
        {
            id: 'buyer',
            header: 'Käufer',
            cell: ({ row }) => {
                const buyer = row.original.buyerCustomer;
                if (!buyer) {
                    return <span className='text-xs text-muted-foreground'>-</span>;
                }
                return (
                    <div>
                        <div
                            className='cursor-pointer hover:underline font-medium text-sm'
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/${buyer.id}`);
                            }}
                        >
                            {buyer.firstName} {buyer.lastName}
                        </div>
                        {buyer.email && (
                            <div className='text-xs text-muted-foreground'>{buyer.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'recipient',
            header: 'Empfänger',
            cell: ({ row }) => {
                const recipient = row.original.recipientCustomer;
                if (!recipient) {
                    return <span className='text-xs text-muted-foreground'>-</span>;
                }
                return (
                    <div>
                        <div
                            className='cursor-pointer hover:underline font-medium text-sm'
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/${recipient.id}`);
                            }}
                        >
                            {recipient.firstName} {recipient.lastName}
                        </div>
                        {recipient.email && (
                            <div className='text-xs text-muted-foreground'>{recipient.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'value',
            header: 'Wert',
            cell: ({ row }) => (
                <span className='font-mono text-sm'>
                    {formatCurrency(row.original.initialValueEur)}
                </span>
            ),
        },
        {
            accessorKey: 'remainingBalance',
            header: 'Restguthaben',
            cell: ({ row }) => (
                <span className={row.original.remainingBalance > 0 ? 'text-green-600 font-medium text-sm' : 'text-muted-foreground text-sm'}>
                    {formatCurrency(row.original.remainingBalance)}
                </span>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                if (row.original.deletedAt) {
                    return (
                        <Badge variant='outline' className='bg-red-50 text-red-700 border-red-200'>
                            Gelöscht
                        </Badge>
                    );
                }
                if (row.original.usedAmount === 0 && row.original.remainingBalance === row.original.initialValueEur) {
                    return (
                        <Badge variant='outline' className='bg-blue-50 text-blue-700 border-blue-200'>
                            Neu
                        </Badge>
                    );
                }
                if (row.original.remainingBalance > 0) {
                    return (
                        <Badge variant='default' className='bg-green-100 text-green-800 hover:bg-green-100'>
                            Aktiv
                        </Badge>
                    );
                }
                return (
                    <Badge variant='outline'>
                        Aufgebraucht
                    </Badge>
                );
            },
        },
    ];

    const table = useReactTable({
        data: giftCards,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
    });

    if (isLoading) {
        return (
            <div className='space-y-2'>
                {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className='h-12 w-full' />
                ))}
            </div>
        );
    }

    if (giftCards.length === 0) {
        return (
            <div className='text-center py-8 text-muted-foreground'>
                Keine Gutscheinkarten vorhanden
            </div>
        );
    }

    return (
        <div className='rounded-md border'>
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((row) => (
                        <>
                            <TableRow
                                key={row.id}
                                className='cursor-pointer hover:bg-muted/50'
                                onClick={() => row.toggleExpanded()}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                            {row.getIsExpanded() && (
                                <TableRow key={`${row.id}-expanded`}>
                                    <TableCell colSpan={columns.length} className='p-0'>
                                        <ExpandedRowContent
                                            giftCard={row.original}
                                            isAdmin={isAdmin}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default CustomerGiftCardsTable;
